import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Rating,
  IconButton,
  Button,
  Avatar,
  Divider,
  Collapse,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ResolveIcon,
  Cancel as UnresolveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  AccessTime as TimeIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { feedbackService, authService } from '../utils/api';
import FeedbackForm from './FeedbackForm';

const FEEDBACK_TYPE_COLORS = {
  general: 'default',
  quality: 'primary',
  accuracy: 'secondary',
  improvement: 'warning'
};

const FEEDBACK_TYPE_LABELS = {
  general: 'General',
  quality: 'Quality',
  accuracy: 'Accuracy',
  improvement: 'Improvement'
};

const FeedbackCard = ({ 
  feedback, 
  onFeedbackUpdated, 
  onFeedbackDeleted, 
  showCallId = false,
  compact = false 
}) => {
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const userRole = authService.getUserRole();
  const isSuperAdmin = userRole === 'super_admin';
  const isOwner = feedback.user_id === parseInt(localStorage.getItem('userId'));

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setShowEditForm(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await feedbackService.deleteFeedback(feedback.id);
      toast.success('Feedback deleted successfully!');
      if (onFeedbackDeleted) {
        onFeedbackDeleted(feedback.id);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete feedback');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleResolveToggle = async () => {
    if (!isSuperAdmin) {
      toast.error('Only super admins can resolve feedback');
      return;
    }

    setLoading(true);
    try {
      const newResolvedState = !feedback.is_resolved;
      const updatedFeedback = await feedbackService.resolveFeedback(feedback.id, newResolvedState);
      toast.success(`Feedback ${newResolvedState ? 'resolved' : 'unresolved'} successfully!`);
      if (onFeedbackUpdated) {
        onFeedbackUpdated(updatedFeedback);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update feedback status');
    } finally {
      setLoading(false);
    }
    handleMenuClose();
  };

  const handleFeedbackSubmitted = (updatedFeedback) => {
    setShowEditForm(false);
    if (onFeedbackUpdated) {
      onFeedbackUpdated(updatedFeedback);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleCallLinkClick = () => {
    if (showCallId && feedback.call_id) {
      window.open(`/call-logs/${feedback.call_id}`, '_blank');
    }
  };

  return (
    <>
      <Card 
        variant="outlined" 
        sx={{ 
          mb: 2,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: 2,
            borderColor: 'primary.main'
          },
          ...(compact && { 
            '& .MuiCardContent-root': { pb: 1 },
            '& .MuiCardActions-root': { pt: 0 }
          })
        }}
      >
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={FEEDBACK_TYPE_LABELS[feedback.feedback_type]}
                color={FEEDBACK_TYPE_COLORS[feedback.feedback_type]}
                size="small"
              />
              {feedback.is_resolved && (
                <Chip
                  label="Resolved"
                  color="success"
                  size="small"
                  variant="outlined"
                />
              )}
              {feedback.rating && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Rating value={feedback.rating} readOnly size="small" />
                  <Typography variant="caption" color="text.secondary">
                    ({feedback.rating}/5)
                  </Typography>
                </Box>
              )}
            </Box>
            
            <IconButton
              size="small"
              onClick={handleMenuClick}
              disabled={loading}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>

          {/* Call ID (if shown) */}
          {showCallId && (
            <Box sx={{ mb: 2 }}>
              <Button
                size="small"
                startIcon={<PhoneIcon />}
                onClick={handleCallLinkClick}
                sx={{ 
                  textTransform: 'none',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }}
              >
                {feedback.call_id}
              </Button>
            </Box>
          )}

          {/* Feedback Text */}
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
            {compact && !expanded ? truncateText(feedback.feedback_text) : feedback.feedback_text}
          </Typography>

          {/* Expand/Collapse for long text */}
          {compact && feedback.feedback_text.length > 150 && (
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ mb: 1 }}
            >
              {expanded ? 'Show Less' : 'Show More'}
            </Button>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 24, height: 24 }}>
                <PersonIcon fontSize="small" />
              </Avatar>
              <Typography variant="caption" color="text.secondary">
                {feedback.user_email}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimeIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {formatDate(feedback.created_at)}
                {feedback.updated_at !== feedback.created_at && (
                  <span> • Edited</span>
                )}
              </Typography>
            </Box>
          </Box>

          {/* Resolution info */}
          {feedback.is_resolved && feedback.resolved_by_email && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
              <Typography variant="caption" color="success.main">
                ✓ Resolved by {feedback.resolved_by_email}
                {feedback.resolved_at && (
                  <span> on {formatDate(feedback.resolved_at)}</span>
                )}
              </Typography>
            </Box>
          )}
        </CardContent>

        {/* Quick Actions */}
        <CardActions sx={{ pt: 0, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isOwner && (
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                disabled={loading}
              >
                Edit
              </Button>
            )}
            {isSuperAdmin && (
              <Button
                size="small"
                startIcon={feedback.is_resolved ? <UnresolveIcon /> : <ResolveIcon />}
                onClick={handleResolveToggle}
                disabled={loading}
                color={feedback.is_resolved ? 'warning' : 'success'}
              >
                {feedback.is_resolved ? 'Unresolve' : 'Resolve'}
              </Button>
            )}
          </Box>
          
          {showCallId && (
            <Button
              size="small"
              startIcon={<LinkIcon />}
              onClick={handleCallLinkClick}
            >
              View Call
            </Button>
          )}
        </CardActions>
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {isOwner && [
          <MenuItem key="edit" onClick={handleEdit}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit Feedback
          </MenuItem>,
          <MenuItem key="delete" onClick={handleDelete}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete Feedback
          </MenuItem>
        ]}
        
        {isSuperAdmin && (
          <MenuItem onClick={handleResolveToggle}>
            {feedback.is_resolved ? (
              <>
                <UnresolveIcon sx={{ mr: 1 }} fontSize="small" />
                Mark as Unresolved
              </>
            ) : (
              <>
                <ResolveIcon sx={{ mr: 1 }} fontSize="small" />
                Mark as Resolved
              </>
            )}
          </MenuItem>
        )}
      </Menu>

      {/* Edit Form Dialog */}
      <FeedbackForm
        open={showEditForm}
        onClose={() => setShowEditForm(false)}
        callId={feedback.call_id}
        feedbackToEdit={feedback}
        onFeedbackSubmitted={handleFeedbackSubmitted}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Feedback</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this feedback? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeedbackCard; 